import React, { useEffect, useState } from "react";
import emailjs from "emailjs-com";

const backgroundImages = [
  "/images/home0.png",
  "/images/home1.jpeg",
  "/images/home2.jpeg",
  "/images/home3.jpg",
  "/images/home4.jpg",
  "/images/home5.jpg",
  "/images/home6.jpg",
];

const highlights = [
  "24/7 attentive support in a warm residential setting",
  "Private, dignified care tailored to each resident",
  "Home-cooked meals, companionship, and daily comfort",
];

const carePoints = [
  {
    title: "Personalized Daily Care",
    text: "Support with bathing, grooming, dressing, mobility, and medication routines delivered with patience and respect.",
  },
  {
    title: "A Safe, Comfortable Home",
    text: "A calm environment in Everett designed around familiarity, cleanliness, fall awareness, and peace of mind for families.",
  },
  {
    title: "Real Connection",
    text: "Companionship matters. We focus on conversation, encouragement, and a steady sense of belonging every day.",
  },
];

const stats = [
  { value: "24/7", label: "Supervision and support" },
  { value: "Everett", label: "Convenient Washington location" },
  { value: "1:1", label: "Attention to resident needs" },
];

export default function HomePage() {
  const [bgIndex, setBgIndex] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    emailjs.init("5Iy2UzsRP9fz9ccQi");
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsSending(true);

    emailjs
      .sendForm("default_service", "template_bqouktd", event.target)
      .then(() => {
        setShowToast(true);
        event.target.reset();
      })
      .catch((error) => {
        console.error("EmailJS error:", error);
        alert("We couldn't send your message right now. Please call us at 206-407-7484.");
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <div className="site-shell">
      <section className="hero-section">
        <div className="hero-slides" aria-hidden="true">
          {backgroundImages.map((image, index) => (
            <div
              key={image}
              className={`hero-slide ${bgIndex === index ? "is-active" : ""}`}
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}
        </div>

        <div className="hero-overlay" />

        <div className="container hero-content">
          <div className="hero-copy">
            <span className="eyebrow">Zagol Seniors Care</span>
            <h1>Thoughtful senior care that feels like home.</h1>
            <p className="hero-lead">
              Compassionate adult family home care in Everett, Washington, with
              a focus on dignity, safety, and peace of mind for the whole
              family.
            </p>

            <div className="hero-actions">
              <a className="btn btn-brand" href="tel:2064077484">
                Call 206-407-7484
              </a>
              <a className="btn btn-outline-light btn-soft" href="#contact">
                Schedule a Conversation
              </a>
            </div>

            <ul className="hero-highlights">
              {highlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <aside className="hero-card">
            <p className="hero-card-label">Why families choose us</p>
            <h2>Warm care, consistent communication, and a setting built for comfort.</h2>
            <div className="stat-grid">
              {stats.map((stat) => (
                <div key={stat.label} className="stat-card">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">About Us</span>
            <h2>Care centered on dignity, familiarity, and daily peace.</h2>
            <p>
              Zagol Seniors Care provides a nurturing residential environment
              where seniors receive attentive support without losing the warmth
              and comfort of home. We care deeply about each resident's routine,
              independence, and emotional well-being.
            </p>
          </div>

          <div className="feature-grid">
            {carePoints.map((point) => (
              <article key={point.title} className="feature-card">
                <h3>{point.title}</h3>
                <p>{point.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block section-tinted">
        <div className="container two-column-layout">
          <div>
            <span className="eyebrow">What You Can Expect</span>
            <h2>A calm, dependable setting for residents and reassurance for loved ones.</h2>
            <p>
              From daily personal care to companionship and health coordination,
              our approach is designed to help residents feel secure and known.
            </p>
          </div>

          <div className="expectation-list">
            <div className="expectation-item">
              <strong>Consistent routines</strong>
              <span>Thoughtful daily structure that supports comfort and stability.</span>
            </div>
            <div className="expectation-item">
              <strong>Family peace of mind</strong>
              <span>Responsive communication and a team that takes trust seriously.</span>
            </div>
            <div className="expectation-item">
              <strong>Respectful care</strong>
              <span>Support delivered with patience, privacy, and personal attention.</span>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="section-block">
        <div className="container contact-grid">
          <div className="contact-panel">
            <span className="eyebrow">Contact Us</span>
            <h2>Let's talk about your loved one's care needs.</h2>
            <p>
              Reach out to ask questions, learn about availability, or schedule
              a visit. We're happy to help you understand whether Zagol Seniors
              Care is the right fit.
            </p>

            <div className="contact-details">
              <a href="tel:2064077484">206-407-7484</a>
              <span>714 88th St SE, Everett, WA 98012</span>
            </div>
          </div>

          <div className="contact-form-card">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="name" className="form-label">
                  Your Name
                </label>
                <input type="text" id="name" name="name" className="form-control" required />
              </div>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Your Email
                </label>
                <input type="email" id="email" name="email" className="form-control" required />
              </div>
              <div className="mb-3">
                <label htmlFor="message" className="form-label">
                  How can we help?
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  className="form-control"
                  required
                />
              </div>
              <button type="submit" className="btn btn-brand w-100" disabled={isSending}>
                {isSending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="section-block map-section">
        <div className="container">
          <div className="map-card">
            <div className="map-copy">
              <span className="eyebrow">Location</span>
              <h2>Conveniently located in Everett, Washington.</h2>
              <p>
                Families can find us at 714 88th St SE, Everett, WA 98012.
              </p>
            </div>
            <iframe
              title="Zagol Seniors Care location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2689.537205032782!2d-122.22088302500015!3d47.93003357109279!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x549aa1db64e934c7%3A0xf7c7e4042d7a9a52!2s714%2088th%20St%20SE%2C%20Everett%2C%20WA%2098012!5e0!3m2!1sen!2sus!4v1720648553816!5m2!1sen!2sus"
              width="100%"
              height="360"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <div className="position-fixed bottom-0 end-0 p-3 toast-wrap">
        <div
          className={`toast align-items-center text-bg-success ${showToast ? "show" : "hide"}`}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div className="d-flex">
            <div className="toast-body">Your message has been sent successfully.</div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              aria-label="Close"
              onClick={() => setShowToast(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
